import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import type { CSSProperties } from "react";

import { Icon } from "@/ds";
import {
  ARVORE_COLUNA_ALTERACAO,
  ARVORE_COLUNA_NOME,
  ARVORE_COLUNA_PERCENTUAL,
  ARVORE_EXPANDIR,
  ARVORE_LABEL,
  ARVORE_RECOLHER,
  ARVORE_TOOLTIP_OPTIONS,
} from "./distribuicao-arvore.constants";
import styles from "./distribuicao-arvore.module.scss";
import type { DistribuicaoArvoreViewModel } from "./types-distribuicao-arvore";

const VARIANTES = {
  desktop: "",
  mobile: styles.arvoreMobile,
  "mobile-ampliada": `${styles.arvoreMobile} ${styles.nomeAmpliado}`,
} as const;

export function DistribuicaoArvoreView({
  linhas,
  variante,
  alternar,
  aoFocar,
  aoTeclar,
}: DistribuicaoArvoreViewModel) {
  return (
    <div className={`${styles.arvore} ${VARIANTES[variante]}`} data-testid="distribuicoes-arvore">
      <div className={styles.cabecalho} aria-hidden="true">
        <span className={styles.celula}>{ARVORE_COLUNA_NOME}</span>
        <span className={`${styles.celula} ${styles.percentual}`}>{ARVORE_COLUNA_PERCENTUAL}</span>
        <span className={styles.celula}>{ARVORE_COLUNA_ALTERACAO}</span>
        <span className={styles.celula} />
      </div>
      <div role="tree" aria-label={ARVORE_LABEL}>
        {linhas.map(
          ({ no, nivel, temFilhos, expandido, posicao, irmaos, focavel, testId, toggleTestId }) => {
            const destaque = temFilhos ? styles.nomeDestaque : "";
            const destaquePercentual = temFilhos ? styles.percentualDestaque : "";
            return (
              <div
                key={no.id}
                role="treeitem"
                aria-level={nivel + 1}
                aria-posinset={posicao}
                aria-setsize={irmaos}
                aria-expanded={temFilhos ? expandido : undefined}
                tabIndex={focavel ? 0 : -1}
                data-no-id={no.id}
                data-testid={testId}
                className={`${styles.linha} ${no.inativo ? styles.linhaInativa : ""}`}
                onFocus={(evento) => aoFocar(evento, no.id)}
                onKeyDown={(evento) => aoTeclar(evento, no.id)}
              >
                <span
                  className={`${styles.celula} ${styles.nome}`}
                  style={{ "--nivel": nivel } as CSSProperties}
                >
                  {temFilhos ? (
                    <Button
                      type="button"
                      className={styles.toggle}
                      icon={<Icon name={expandido ? "chevron-down" : "chevron-right"} size={14} />}
                      aria-label={`${expandido ? ARVORE_RECOLHER : ARVORE_EXPANDIR} ${no.rotulo}`}
                      tabIndex={-1}
                      onClick={() => alternar(no.id)}
                      severity="secondary"
                      text
                      rounded
                      data-testid={toggleTestId}
                    />
                  ) : (
                    <span className={styles.toggleVazio} aria-hidden="true" />
                  )}
                  <span className={`${styles.nomeTexto} ${destaque}`}>{no.rotulo}</span>
                  {no.tags.map((tag) => (
                    <Tag key={tag} value={tag} severity="secondary" />
                  ))}
                </span>
                <span className={`${styles.celula} ${styles.percentual} ${destaquePercentual}`}>
                  {no.percentual}
                </span>
                <span className={`${styles.celula} ${styles.alteracao}`}>{no.ultimaAlteracao}</span>
                <span className={`${styles.celula} ${styles.acoes}`}>
                  {no.editar ? (
                    <Button
                      type="button"
                      icon={<Icon name="pencil" size={14} />}
                      aria-label={no.editar.label}
                      tooltip={no.editar.tooltip}
                      tooltipOptions={ARVORE_TOOLTIP_OPTIONS}
                      onClick={no.editar.executar}
                      severity="secondary"
                      text
                      rounded
                      data-testid={no.editar.testId}
                    />
                  ) : null}
                  {no.situacao ? (
                    <Button
                      type="button"
                      icon={<Icon name="power-off" size={14} />}
                      aria-label={no.situacao.label}
                      tooltip={no.situacao.tooltip}
                      tooltipOptions={ARVORE_TOOLTIP_OPTIONS}
                      onClick={no.situacao.executar}
                      severity="secondary"
                      text
                      rounded
                      data-testid={no.situacao.testId}
                    />
                  ) : null}
                </span>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
}
